import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { getBounds } from '/@/core/fields';
import settings from '/@/core/settings';
import { useCatalogProductById } from '/@/hooks/catalog';
import { useLastCampaignsBulk } from '/@/hooks/crop-monitoring/use-last-campaign';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useMyFields, useMyFieldsByProduct } from '/@/hooks/fields';
import { useOwnUserNode, useUserNodes } from '/@/hooks/nodes';
import { useSimplifiedDefaultCropConfig } from '/@/hooks/recommendation';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import useWindowSize from '/@/utils/hooks/window-size';
import { reverse } from '/@/utils/router';

export type FieldSelectTab = 'growers' | 'my-fields';

type UseFieldSelectOptions = {
    field: Field | undefined;
    setField: (field: Field | undefined) => void;
    /** Enables the valid-polygon filter feature (banner, toggle, point-field disabling). */
    polygonFilter?: boolean;
    selectedProduct?: number;
    soilMoisture?: number;
    plantingDate?: string;
};

export function useFieldSelect({
    field,
    setField,
    polygonFilter,
    selectedProduct,
    soilMoisture,
    plantingDate,
}: UseFieldSelectOptions) {
    const navigate = useNavigate();
    const isMobile = useWindowSize().width <= 900;
    const country = useSelector(getSessionCountry);

    const [fieldName, setFieldName] = useState('');
    const [tab, setTab] = useState<FieldSelectTab>('growers');
    const [selectedUserNodeId, setSelectedUserNodeId] = useState<number | null>(null);
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
    const [newGrowersModalOpen, setNewGrowersModalOpen] = useState(false);

    const debouncedFieldName = useDebounce(fieldName, 1000);

    const [searchParams] = useSearchParams();
    const userNodeId = searchParams.get('userNodeId') ? Number(searchParams.get('userNodeId')) : undefined;

    const ownNodeQuery = useOwnUserNode();
    const growersQuery = useUserNodes({ type: 'grower', page: 1, page_size: 10000 });
    const isBayer = useIsValidatedBayerUserType();

    const { data: product } = useCatalogProductById(
        { id: selectedProduct, crop: 'corn' },
        { enabled: !!selectedProduct }
    );
    const cropConfig = useSimplifiedDefaultCropConfig({
        crop: 'corn',
        zones: product?.zones || [],
        countryCode: country?.code,
    });

    const includeChildrenNodes = !userNodeId;
    const showGrowersFields = !userNodeId && isBayer && tab === 'growers';
    const growersDataReady = growersQuery.data !== undefined;
    const isLoadingGrowers = showGrowersFields && !growersDataReady;
    const hasNoGrowers = showGrowersFields && growersDataReady && (growersQuery.data?.count ?? 0) === 0;

    const fieldModalGrowerUserNodeId = useMemo(() => {
        if (userNodeId) return userNodeId;
        if (tab === 'my-fields') return ownNodeQuery.data?.id;
        if (tab === 'growers' && selectedUserNodeId) return selectedUserNodeId;
        return undefined;
    }, [userNodeId, tab, ownNodeQuery.data?.id, selectedUserNodeId]);

    const nodeScope = userNodeId || selectedUserNodeId || undefined;
    const includeChildrenScope = includeChildrenNodes && tab === 'growers';
    const excludeTargetNodeScope = showGrowersFields && !selectedUserNodeId && tab === 'growers';

    const fieldsQuery = useMyFields(
        {
            page: 1,
            pageSize: 10000,
            country: country.code,
            selectOnly: 'id,lat,lng,name,location,altitude,area,geometry,externalProvider,node',
            name: debouncedFieldName,
            geometryType: polygonFilter ? 'polygon' : undefined,
            userNodeId: nodeScope,
            includeChildren: includeChildrenScope,
            excludeTargetNode: excludeTargetNodeScope,
        },
        { enabled: !selectedProduct }
    );

    const productFieldsQuery = useMyFieldsByProduct(
        {
            page: 1,
            pageSize: 10000,
            country: country?.code,
            product_id: product?.productId,
            climate_pattern: cropConfig.climatePattern,
            planting_date: plantingDate || cropConfig.plantingDate,
            soil_moisture: soilMoisture?.toString(),
            userNodeId: nodeScope,
            includeChildren: includeChildrenScope,
            excludeTargetNode: excludeTargetNodeScope,
        },
        { enabled: !!product?.productId && !!cropConfig.climatePattern && !!(plantingDate || cropConfig.plantingDate) }
    );

    const { lastCampaigns } = useLastCampaignsBulk({
        node: nodeScope,
        includeChildren: includeChildrenScope,
        excludeTargetNode: excludeTargetNodeScope,
    });

    useEffect(() => {
        if (tab === 'my-fields') {
            setSelectedUserNodeId(null);
        }
    }, [tab]);

    useEffect(() => {
        if (productFieldsQuery.error?.code) {
            enqueueSnackbar(
                _(
                    'No se encontraron lotes que coincidan con la zona del producto elegido. Por favor elige otro producto.'
                ),
                { variant: 'error' }
            );
        }
    }, [productFieldsQuery.error?.code]);

    const fieldList: Field[] = productFieldsQuery.data?.results || fieldsQuery.data?.results || [];
    const activeFieldsQuery = selectedProduct ? productFieldsQuery : fieldsQuery;
    const isLoadingFields = activeFieldsQuery.isLoading || (activeFieldsQuery.isValidating && !activeFieldsQuery.data);
    const showMyFieldsEmpty = !isLoadingFields && !fieldList.length && !debouncedFieldName && tab === 'my-fields';

    const mapRef = useRef(null);
    const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const focusMap = () => {
        const bounds = new google.maps.LatLngBounds();
        if (!mapRef.current) return;
        if (field) {
            mapRef.current.fitBounds(getBounds(field.geometry));
            return;
        }
        if (fieldList.length > 0) {
            fieldList.forEach(({ lat, lng }) => bounds.extend(new google.maps.LatLng(lat, lng)));
            mapRef.current.fitBounds(bounds);
            setTimeout(
                ({ mapRef }) => {
                    if (!mapRef.current) return;
                    if (mapRef.current.getZoom() > 16) mapRef.current.setZoom(16);
                },
                100,
                { mapRef }
            );
            return;
        }
        if (country) mapRef.current.fitBounds(country.bounds);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: focusMap reads fieldList/field/country inline
    useEffect(() => {
        focusMap();
    }, [fieldList, field]);

    const handleFieldClick = (clickedField: Field) => {
        setField(clickedField);
        fieldRefs.current[clickedField.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const handleAddField = (growerUserNodeId?: number) => {
        if (growerUserNodeId) setSelectedUserNodeId(growerUserNodeId);
        if (settings.app.theme === 'expertobayer') {
            setNewFieldModalOpen(true);
            return;
        }
        mpTrackEvent('Field create began', { entry_source: 'recommendation-create' });
        navigate(
            growerUserNodeId
                ? reverse('fields.NewField', { qs: { userNodeId: growerUserNodeId } })
                : reverse('fields.NewField')
        );
    };

    const handleGrowerSavedAndAddFields = (growerUserNodeId?: number) => {
        setNewGrowersModalOpen(false);
        handleAddField(growerUserNodeId);
    };

    return {
        tab,
        setTab,
        fieldName,
        setFieldName,
        debouncedFieldName,
        fieldList,
        field,
        setField,
        selectedProduct,
        product,
        isMobile,
        countryCode: country.code,
        includeChildrenNodes,
        showGrowersFields,
        isLoadingGrowers,
        hasNoGrowers,
        isLoadingFields,
        showMyFieldsEmpty,
        growersQuery,
        selectedUserNodeId,
        setSelectedUserNodeId,
        ownNodeQuery,
        mapRef,
        fieldRefs,
        handleFieldClick,
        handleAddField,
        handleGrowerSavedAndAddFields,
        newFieldModalOpen,
        setNewFieldModalOpen,
        newGrowersModalOpen,
        setNewGrowersModalOpen,
        fieldModalGrowerUserNodeId,
        fieldsQuery,
        lastCampaigns,
    };
}

export type FieldSelectState = ReturnType<typeof useFieldSelect>;
